import { platformStorageAttachedObjectReferenceSchema } from '@crest/platform-contracts/storage';
import { z } from 'zod';
import { insuranceClaimsCommandReasonSchema, insuranceClaimsCommandReceiptSchema, insuranceClaimsIdSchema, insuranceClaimsPageSchema, } from './insurance-claims-claim.contract.js';
import { projectsProjectAccessDecisionSchema, } from '@crest/module-projects/contracts/project-access-decision';
import { projectsProjectReferenceSnapshotSchema, } from '@crest/module-projects/contracts/project-reference';
export const insuranceClaimsAobNoticesCapability = 'insurance-claims.aob-notices.v1';
export const insuranceClaimsAobNoticeVersionsCapability = 'insurance-claims.aob-notice-versions.v1';
const nullableText = (maximum) => z.string().trim().max(maximum).nullable();
const sourceCapabilitySchema = z.string().regex(/^[a-z][a-z0-9-]*(?:\.[a-z0-9-]+)+\.v[1-9][0-9]*$/);
const sha256Schema = z.string().regex(/^[a-f0-9]{64}$/);
export const insuranceClaimsAobNoticeTypeSchema = z.enum([
    'aob', 'direct_pay', 'aif', 'combined',
]);
export const insuranceClaimsAobNoticeStatusSchema = z.enum([
    'pending', 'generated', 'sent', 'acknowledged', 'cancelled',
]);
export const insuranceClaimsAobEscrSectionSchema = z.enum(['5', '6', '7']);
export const insuranceClaimsAobDeliveryMethodSchema = z.enum([
    'email', 'fax', 'mail', 'hand_delivered',
]);
export const insuranceClaimsOwnerProofReferenceSchema = z.strictObject({
    verification: z.enum(['operator-attested', 'provider-verified']),
    capability: sourceCapabilitySchema,
    sourceId: z.string().trim().min(1).max(240),
    sourceVersion: z.number().int().positive().nullable(),
    contentHash: sha256Schema.nullable(),
    observedAt: z.string().datetime({ offset: true }),
}).superRefine((value, context) => {
    if (value.verification === 'provider-verified' &&
        (value.sourceVersion === null || value.contentHash === null)) {
        context.addIssue({
            code: 'custom',
            path: ['verification'],
            message: 'Provider-verified owner evidence requires an exact version and content hash.',
        });
    }
});
export const insuranceClaimsAobRecipientSnapshotSchema = z.strictObject({
    carrierName: nullableText(300),
    adjusterName: nullableText(200),
    adjusterEmail: z.string().trim().email().max(320).nullable(),
    adjusterFax: nullableText(40),
    relationshipContactId: insuranceClaimsIdSchema.nullable(),
});
export const insuranceClaimsAobInsuredSnapshotSchema = z.strictObject({
    displayName: z.string().trim().min(1).max(300),
    propertyAddress: nullableText(500),
    claimNumber: nullableText(120),
    policyNumber: nullableText(120),
});
export const insuranceClaimsAobArtifactSchema = z.strictObject({
    source: insuranceClaimsOwnerProofReferenceSchema,
    objectReference: platformStorageAttachedObjectReferenceSchema,
});
export const insuranceClaimsAobDeliverySchema = z.strictObject({
    source: insuranceClaimsOwnerProofReferenceSchema,
    method: insuranceClaimsAobDeliveryMethodSchema,
    sentAt: z.string().datetime({ offset: true }),
}).superRefine((value, context) => {
    if (['email', 'fax'].includes(value.method) &&
        value.source.verification !== 'provider-verified') {
        context.addIssue({
            code: 'custom',
            path: ['source', 'verification'],
            message: 'Email and fax delivery require provider-verified Communications evidence.',
        });
    }
});
export const insuranceClaimsAobNoticeDraftSchema = z.strictObject({
    claimId: insuranceClaimsIdSchema,
    noticeType: insuranceClaimsAobNoticeTypeSchema,
    authorizationSource: insuranceClaimsOwnerProofReferenceSchema.nullable(),
    escrSections: z.array(insuranceClaimsAobEscrSectionSchema).min(1).max(3)
        .refine((values) => new Set(values).size === values.length, 'ESCR sections must be unique.'),
    recipientOverride: insuranceClaimsAobRecipientSnapshotSchema.nullable(),
    insuredOverride: insuranceClaimsAobInsuredSnapshotSchema.nullable(),
    dueAt: z.string().datetime({ offset: true }).nullable(),
});
export const insuranceClaimsAobNoticeDataSchema = z.strictObject({
    projectReference: projectsProjectReferenceSnapshotSchema,
    projectAccessDecision: projectsProjectAccessDecisionSchema,
    noticeType: insuranceClaimsAobNoticeTypeSchema,
    authorizationSource: insuranceClaimsOwnerProofReferenceSchema.nullable(),
    escrSections: z.array(insuranceClaimsAobEscrSectionSchema).min(1).max(3),
    recipient: insuranceClaimsAobRecipientSnapshotSchema,
    insured: insuranceClaimsAobInsuredSnapshotSchema,
    artifact: insuranceClaimsAobArtifactSchema.nullable(),
    delivery: insuranceClaimsAobDeliverySchema.nullable(),
    acknowledgementNote: nullableText(4_000),
    cancellationReason: nullableText(4_000),
});
export const insuranceClaimsCreateAobNoticeCommandSchema = z.strictObject({
    kind: z.literal('create'),
    commandId: insuranceClaimsIdSchema,
    noticeId: insuranceClaimsIdSchema,
    notice: insuranceClaimsAobNoticeDraftSchema,
    reason: insuranceClaimsCommandReasonSchema,
});
const noticeLifecycleBase = {
    commandId: insuranceClaimsIdSchema,
    expectedRevision: z.number().int().positive(),
    reason: insuranceClaimsCommandReasonSchema,
};
export const insuranceClaimsReviseAobNoticeCommandSchema = z.strictObject({
    kind: z.literal('revise'),
    ...noticeLifecycleBase,
    notice: insuranceClaimsAobNoticeDraftSchema,
});
export const insuranceClaimsAttachAobArtifactCommandSchema = z.strictObject({
    kind: z.literal('attach-artifact'),
    ...noticeLifecycleBase,
    artifact: insuranceClaimsAobArtifactSchema,
});
export const insuranceClaimsRecordAobDeliveryCommandSchema = z.strictObject({
    kind: z.literal('record-delivery'),
    ...noticeLifecycleBase,
    delivery: insuranceClaimsAobDeliverySchema,
});
export const insuranceClaimsAcknowledgeAobNoticeCommandSchema = z.strictObject({
    kind: z.literal('acknowledge'),
    ...noticeLifecycleBase,
    acknowledgedAt: z.string().datetime({ offset: true }),
    acknowledgementNote: z.string().trim().min(1).max(4_000),
});
export const insuranceClaimsCancelAobNoticeCommandSchema = z.strictObject({
    kind: z.literal('cancel'),
    ...noticeLifecycleBase,
    cancellationReason: z.string().trim().min(10).max(4_000),
});
export const insuranceClaimsAobNoticeCommandSchema = z.discriminatedUnion('kind', [
    insuranceClaimsReviseAobNoticeCommandSchema,
    insuranceClaimsAttachAobArtifactCommandSchema,
    insuranceClaimsRecordAobDeliveryCommandSchema,
    insuranceClaimsAcknowledgeAobNoticeCommandSchema,
    insuranceClaimsCancelAobNoticeCommandSchema,
]);
export const insuranceClaimsAobNoticeVersionSchema = z.strictObject({
    contractVersion: z.literal(insuranceClaimsAobNoticeVersionsCapability),
    tenantId: insuranceClaimsIdSchema,
    id: insuranceClaimsIdSchema,
    noticeId: insuranceClaimsIdSchema,
    versionNumber: z.number().int().positive(),
    status: insuranceClaimsAobNoticeStatusSchema,
    dueAt: z.string().datetime({ offset: true }),
    contentHash: sha256Schema,
    reason: insuranceClaimsCommandReasonSchema,
    actorMembershipId: insuranceClaimsIdSchema,
    createdAt: z.string().datetime({ offset: true }),
    data: insuranceClaimsAobNoticeDataSchema,
});
export const insuranceClaimsAobNoticeSchema = z.strictObject({
    contractVersion: z.literal(insuranceClaimsAobNoticesCapability),
    tenantId: insuranceClaimsIdSchema,
    id: insuranceClaimsIdSchema,
    claimId: insuranceClaimsIdSchema,
    projectId: insuranceClaimsIdSchema,
    status: insuranceClaimsAobNoticeStatusSchema,
    revision: z.number().int().positive(),
    currentVersionId: insuranceClaimsIdSchema,
    dueAt: z.string().datetime({ offset: true }),
    generatedAt: z.string().datetime({ offset: true }).nullable(),
    sentAt: z.string().datetime({ offset: true }).nullable(),
    acknowledgedAt: z.string().datetime({ offset: true }).nullable(),
    cancelledAt: z.string().datetime({ offset: true }).nullable(),
    createdByMembershipId: insuranceClaimsIdSchema,
    updatedByMembershipId: insuranceClaimsIdSchema,
    createdAt: z.string().datetime({ offset: true }),
    updatedAt: z.string().datetime({ offset: true }),
    currentVersion: insuranceClaimsAobNoticeVersionSchema,
});
export const insuranceClaimsAobNoticeListQuerySchema = z.strictObject({
    claimId: insuranceClaimsIdSchema.nullable().default(null),
    projectId: insuranceClaimsIdSchema.nullable().default(null),
    status: insuranceClaimsAobNoticeStatusSchema.nullable().default(null),
    includeCancelled: z.boolean().default(false),
    limit: z.number().int().min(1).max(250).default(50),
    offset: z.number().int().nonnegative().default(0),
});
export const insuranceClaimsAobNoticeResponseSchema = z.strictObject({
    data: insuranceClaimsAobNoticeSchema,
    command: insuranceClaimsCommandReceiptSchema.optional(),
});
export const insuranceClaimsAobNoticesResponseSchema = z.strictObject({
    data: z.array(insuranceClaimsAobNoticeSchema),
    page: insuranceClaimsPageSchema,
});
export const insuranceClaimsAobNoticeVersionsResponseSchema = z.strictObject({
    data: z.array(insuranceClaimsAobNoticeVersionSchema),
});
export const insuranceClaimsAobNoticeDashboardSchema = z.strictObject({
    contractVersion: z.literal('insurance-claims.aob-notice-dashboard.v1'),
    asOf: z.string().datetime({ offset: true }),
    pendingCount: z.number().int().nonnegative(),
    generatedCount: z.number().int().nonnegative(),
    overdueCount: z.number().int().nonnegative(),
    active: z.array(insuranceClaimsAobNoticeSchema),
});
export function addInsuranceClaimsBusinessDays(input) {
    const parsedDays = z.number().int().min(1).max(30).parse(input.businessDays);
    const result = new Date(z.string().datetime({ offset: true }).parse(input.from));
    let added = 0;
    while (added < parsedDays) {
        result.setUTCDate(result.getUTCDate() + 1);
        const day = result.getUTCDay();
        if (day !== 0 && day !== 6)
            added += 1;
    }
    return result.toISOString();
}
export function computeInsuranceClaimsAobNoticeDashboard(input) {
    const asOf = z.string().datetime({ offset: true }).parse(input.asOf);
    const active = input.notices
        .filter(({ status }) => status === 'pending' || status === 'generated')
        .sort((left, right) => left.dueAt.localeCompare(right.dueAt));
    return insuranceClaimsAobNoticeDashboardSchema.parse({
        contractVersion: 'insurance-claims.aob-notice-dashboard.v1',
        asOf,
        pendingCount: active.filter(({ status }) => status === 'pending').length,
        generatedCount: active.filter(({ status }) => status === 'generated').length,
        overdueCount: active.filter(({ dueAt }) => dueAt < asOf).length,
        active,
    });
}
//# sourceMappingURL=insurance-claims-aob-notice.contract.js.map